(() => {
  'use strict';
  if (window.BeautyBratsRewards) {
    window.BeautyBratsRewards.mount();
    return;
  }

  const controllers = new WeakMap();
  const text = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = String(value);
    return node;
  };
  const integer = value => Number.isSafeInteger(Number(value)) ? Number(value) : 0;
  const storage = {
    get(key) {
      try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; }
    },
    set(key, value) {
      try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
    },
    remove(key) {
      try { sessionStorage.removeItem(key); } catch {}
    }
  };
  const requestKey = () => {
    if (crypto.randomUUID) return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const h = Array.from(bytes, v => v.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };

  class Rewards {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.mode;
      this.base = root.dataset.apiBase;
      if (!/^\/apps\/[a-z0-9-]+$/.test(this.base || '')) return;
      this.words = JSON.parse(root.querySelector('[data-rewards-i18n]').textContent);
      this.body = root.querySelector('[data-rewards-body]');
      this.dialog = root.querySelector('dialog');
      this.launcher = root.querySelector('[data-rewards-open]');
      this.loginUrl = root.dataset.loginUrl || '/pages/sign-in?from=rewards';
      this.account = null;
      this.loading = false;
      this.config = null;
      this.pendingStorage = 'bb_rewards_pending_redemption_v1';
      this.pending = storage.get(this.pendingStorage);
      if (!this.pending || !Number.isSafeInteger(this.pending.points) || this.pending.points < 100 || !/^[a-f\d-]{36}$/i.test(this.pending.key || '')) {
        this.pending = null;
        storage.remove(this.pendingStorage);
      }
      this.captureReferral();
      this.launcher?.addEventListener('click', () => this.open());
      root.querySelector('[data-rewards-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      this.dialog?.addEventListener('close', () => this.launcher?.focus());
      window.addEventListener('bb:rewards-changed', event => {
        if (event.detail !== this.root.id && (this.mode === 'inline' || this.dialog?.open)) this.loadAccount();
      });
      this.initialize();
    }

    t(key, values = {}) {
      let value = this.words[key] || this.words.generic_error || '';
      for (const [name, replacement] of Object.entries(values)) value = value.replaceAll(`{${name}}`, String(replacement));
      return value;
    }

    money(value) {
      const currency = this.account?.currency || this.config?.currency || 'USD';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(value) || 0); }
      catch { return `US $${(Number(value) || 0).toFixed(2)}`; }
    }

    points(value) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en').format(integer(value));
    }

    async api(path, payload) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(`${this.base}${path}`, {
          method: payload ? 'POST' : 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
          body: payload ? JSON.stringify(payload) : undefined,
          signal: controller.signal
        });
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) throw Object.assign(new Error(this.t('unavailable')), { status: response.status });
        const data = await response.json();
        if (!response.ok) {
          const message = response.status === 401 || response.status === 403 ? this.t('session_error') : (typeof data.message === 'string' ? data.message : this.t('generic_error'));
          throw Object.assign(new Error(message), { status: response.status, errorCode: data.error });
        }
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error(this.t('unavailable'));
        return data;
      } catch (error) {
        if (error.status) throw error;
        throw Object.assign(new Error(this.t('network_error')), { status: 0 });
      } finally { clearTimeout(timeout); }
    }

    async initialize() {
      try {
        this.config = await this.api('/config');
        if (this.config.enabled !== true) {
          if (this.mode === 'inline') this.showUnavailable('disabled');
          return;
        }
        if (this.launcher) this.launcher.hidden = false;
        if (this.mode === 'inline') await this.loadAccount();
      } catch {
        if (this.mode === 'inline') this.showUnavailable();
      }
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
      this.loadAccount();
    }

    async loadAccount() {
      if (this.loading) return;
      this.loading = true;
      this.body.setAttribute('aria-busy', 'true');
      if (!this.account) this.body.replaceChildren(text('p', 'bb-rewards__loading', this.t('loading')));
      try {
        const account = await this.api('/account');
        if (account.enabled !== true) { this.showUnavailable('disabled'); return; }
        this.account = account;
        this.render();
      } catch (error) {
        if (error.status === 401 || error.status === 403) {
          this.account = { enabled: true, authenticated: false };
          this.render();
        } else this.showUnavailable();
      } finally {
        this.loading = false;
        this.body.setAttribute('aria-busy', 'false');
      }
    }

    showUnavailable(key = 'unavailable') {
      this.body.replaceChildren();
      const panel = this.card();
      panel.append(text('p', 'bb-rewards__muted', this.t(key)), this.button(this.t('retry'), () => this.initialize(), 'secondary'));
      this.body.append(panel);
      this.body.setAttribute('aria-busy', 'false');
    }

    card(title, extra = '') {
      const card = text('section', `bb-rewards__card ${extra}`.trim());
      if (title) card.append(text('h3', '', title));
      return card;
    }

    button(label, handler, variant = '') {
      const button = text('button', `bb-rewards__button ${variant ? `bb-rewards__button--${variant}` : ''}`.trim(), label);
      button.type = 'button';
      if (handler) button.addEventListener('click', handler);
      return button;
    }

    notice(value, kind = '') {
      const node = text('p', `bb-rewards__notice ${kind ? `bb-rewards__notice--${kind}` : ''}`.trim(), value);
      node.setAttribute('role', kind === 'error' ? 'alert' : 'status');
      return node;
    }

    login() {
      const link = text('a', 'bb-rewards__button bb-rewards__button--wide', this.t('sign_in'));
      const url = new URL(this.loginUrl, location.origin);
      if (['http:', 'https:'].includes(url.protocol)) link.href = url.href;
      link.rel = 'nofollow';
      return link;
    }

    render() {
      this.body.replaceChildren();
      this.status = text('div', 'bb-rewards__status');
      this.status.setAttribute('aria-live', 'polite');
      this.body.append(this.status);
      if (!this.account.authenticated) {
        const guest = this.card(this.t('join_title'), 'bb-rewards__guest');
        guest.append(text('p', 'bb-rewards__muted', this.t('join_body')), this.login());
        this.body.append(guest, this.earnCard());
        return;
      }
      const balance = this.card('', 'bb-rewards__card--balance');
      const top = text('div', 'bb-rewards__balance-top');
      top.append(text('p', '', this.t('balance')));
      const refresh = this.button(this.t('refresh'), () => this.loadAccount());
      refresh.className = 'bb-rewards__text-button';
      top.append(refresh);
      const number = text('div', 'bb-rewards__balance');
      number.append(text('strong', '', this.points(this.account.points)), text('span', '', this.t('points')));
      balance.append(top, number, text('p', 'bb-rewards__muted', this.t('balance_note')));
      this.body.append(balance, this.redeemCard(), this.earnCard(), this.couponsCard());
      if (this.account.referral?.enabled === true) this.body.append(this.referralCard());
      this.body.append(this.historyCard());
    }

    earnCard() {
      const points = integer(this.account?.pointsPerDollar ?? this.config?.pointsPerDollar) || 1;
      const card = this.card(this.t('earn_title'));
      card.append(text('p', 'bb-rewards__rule', this.t(points === 1 ? 'earn_rule' : 'earn_rule_plural', { points, amount: this.money(1) })), text('p', 'bb-rewards__muted', this.t('earn_note')));
      return card;
    }

    redeemCard() {
      const unit = integer(this.account.redemptionPoints) || 100;
      const balance = Math.max(0, integer(this.account.points));
      const maximum = Math.floor(balance / unit) * unit;
      const card = this.card(this.t('redeem_title'));
      card.append(text('p', 'bb-rewards__rule', this.t('redeem_rule', { points: this.points(unit), amount: this.money(1) })));
      if (maximum < unit && !this.pending) {
        card.append(text('p', 'bb-rewards__muted', this.t('redeem_minimum', { points: this.points(unit) })));
        return card;
      }
      const form = text('form', 'bb-rewards__form');
      const inputId = `${this.root.id}-redeem-points`;
      const label = text('label', '', this.t('redeem_label'));
      label.htmlFor = inputId;
      const input = text('input', 'bb-rewards__input');
      Object.assign(input, { type: 'number', id: inputId, name: 'points', inputMode: 'numeric', min: String(unit), max: String(Math.max(maximum, this.pending?.points || 0)), step: String(unit), value: String(this.pending?.points || unit), required: true });
      input.disabled = Boolean(this.pending);
      const value = text('div', 'bb-rewards__value');
      const amount = text('strong', '', this.money(Number(input.value) / unit));
      value.append(text('span', '', this.t('reward_value')), amount);
      input.addEventListener('input', () => { amount.textContent = this.money(Math.max(0, integer(input.value)) / unit); });
      const submit = this.button(this.t(this.pending ? 'redeem_retry' : 'redeem_button'), null, 'wide');
      submit.type = 'submit';
      const message = text('div', '');
      message.setAttribute('aria-live', 'polite');
      form.append(label, input, text('p', 'bb-rewards__muted', this.t('redeem_note', { points: this.points(unit) })), value, submit, message);
      if (this.pending) card.append(this.notice(this.t('redeem_pending')));
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (submit.disabled) return;
        const points = this.pending?.points || integer(input.value);
        if (!this.pending && (points < unit || points % unit !== 0 || points > maximum)) { message.replaceChildren(this.notice(this.t('redeem_invalid', { points: this.points(unit) }), 'error')); return; }
        if (!this.account.csrfToken) { message.replaceChildren(this.notice(this.t('session_error'), 'error'), this.login()); return; }
        if (!this.pending) {
          this.pending = { points, key: requestKey() };
          storage.set(this.pendingStorage, this.pending);
        }
        input.disabled = true;
        submit.disabled = true;
        submit.textContent = this.t('redeem_busy');
        message.replaceChildren();
        try {
          const result = await this.api('/redeem', { points: this.pending.points, idempotencyKey: this.pending.key, csrfToken: this.account.csrfToken });
          if (!result.code || ['pending', 'processing', 'reserved'].includes(String(result.status).toLowerCase())) {
            message.replaceChildren(this.notice(this.t('redeem_processing')));
            return;
          }
          this.pending = null;
          storage.remove(this.pendingStorage);
          await this.loadAccount();
          const success = this.notice(this.t('redeem_success'), 'success');
          success.append(this.copyRow(result.code, this.t('copy')));
          success.tabIndex = -1;
          this.status?.replaceChildren(success);
          success.focus();
          window.dispatchEvent(new CustomEvent('bb:rewards-changed', { detail: this.root.id }));
        } catch (error) {
          const confirmedFailure = error.status >= 400 && error.status < 500 && ![408, 409, 429].includes(error.status);
          if (confirmedFailure && ![401, 403].includes(error.status)) {
            this.pending = null;
            storage.remove(this.pendingStorage);
            input.disabled = false;
          }
          message.replaceChildren(this.notice(error.message, 'error'));
          if ([401, 403].includes(error.status)) message.append(this.login());
        } finally {
          submit.disabled = false;
          submit.textContent = this.t(this.pending ? 'redeem_retry' : 'redeem_button');
        }
      });
      card.append(form);
      return card;
    }

    copyRow(value, label, isUrl = false) {
      const row = text('div', 'bb-rewards__copy-row');
      const code = text('span', `bb-rewards__code${isUrl ? ' bb-rewards__referral-url' : ''}`, String(value));
      code.tabIndex = 0;
      code.setAttribute('aria-label', this.t(isUrl ? 'referral_link' : 'reward_code'));
      const button = this.button(label, async () => {
        try {
          if (!navigator.clipboard?.writeText) throw new Error('clipboard');
          await navigator.clipboard.writeText(String(value));
          button.textContent = this.t('copied');
          setTimeout(() => { button.textContent = label; }, 2000);
        } catch {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(code);
          selection?.removeAllRanges();
          selection?.addRange(range);
          code.focus();
          row.after(this.notice(this.t('copy_failed')));
        }
      }, 'secondary');
      button.classList.add('bb-rewards__button--small');
      row.append(code, button);
      return row;
    }

    couponsCard() {
      const card = this.card(this.t('coupons_title'));
      const coupons = Array.isArray(this.account.redemptions) ? this.account.redemptions.slice(0, 20) : [];
      if (!coupons.length) { card.append(text('p', 'bb-rewards__muted', this.t('coupons_empty'))); return card; }
      const list = text('ul', 'bb-rewards__list');
      for (const coupon of coupons) {
        const item = text('li', '');
        const head = text('div', 'bb-rewards__coupon-head');
        const rawStatus = String(coupon.status || 'pending').toLowerCase();
        const ready = ['ready', 'issued', 'active', 'completed'].includes(rawStatus);
        const status = ready ? 'ready' : ['used', 'cancelled', 'expired'].includes(rawStatus) ? rawStatus : 'pending';
        head.append(text('strong', '', this.money(coupon.amount)), text('span', `bb-rewards__badge${ready ? ' bb-rewards__badge--ready' : ''}`, this.t(`coupon_${status}`)));
        item.append(head, text('p', 'bb-rewards__muted', `${this.points(coupon.points)} ${this.t('points')}`));
        if (coupon.code && ready) item.append(this.copyRow(coupon.code, this.t('copy')));
        list.append(item);
      }
      card.append(list);
      return card;
    }

    historyCard() {
      const card = this.card(this.t('history_title'));
      const history = Array.isArray(this.account.ledger) ? this.account.ledger.slice(0, 20) : [];
      if (!history.length) { card.append(text('p', 'bb-rewards__muted', this.t('history_empty'))); return card; }
      const list = text('ul', 'bb-rewards__list');
      for (const entry of history) {
        const item = text('li', 'bb-rewards__history-row');
        const detail = text('div', '');
        const kind = String(entry.kind || 'other').toLowerCase();
        const key = /order|earn|purchase/.test(kind) ? 'order' : /redeem/.test(kind) ? 'redeem' : /refund|return/.test(kind) ? 'refund' : /migrat|import|opening/.test(kind) ? 'migration' : /referral/.test(kind) ? 'referral' : /adjust/.test(kind) ? 'adjustment' : 'other';
        detail.append(text('strong', '', this.t(`activity_${key}`)));
        const date = new Date(entry.createdAt);
        if (!Number.isNaN(date.valueOf())) {
          const time = text('time', '', new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric', year: 'numeric' }).format(date));
          time.dateTime = date.toISOString();
          detail.append(time);
        }
        const delta = integer(entry.points);
        item.append(detail, text('span', `bb-rewards__history-points${delta < 0 ? ' bb-rewards__history-points--negative' : ''}`, `${delta > 0 ? '+' : ''}${this.points(delta)}`));
        list.append(item);
      }
      card.append(list);
      return card;
    }

    captureReferral() {
      const code = new URL(location.href).searchParams.get('bb_ref');
      if (code && /^[a-z\d_-]{3,80}$/i.test(code)) storage.set('bb_rewards_referral_v1', { code });
    }

    referralCard() {
      const referral = this.account.referral;
      const card = this.card(this.t('referral_title'));
      card.append(text('p', 'bb-rewards__muted', this.t('referral_body', { amount: this.money(referral.rewardAmount || 5) })));
      try {
        const url = new URL(referral.url, location.origin);
        if (url.origin === location.origin && ['http:', 'https:'].includes(url.protocol)) card.append(this.copyRow(url.href, this.t('copy_link'), true));
      } catch {}
      const details = text('details', 'bb-rewards__details');
      details.append(text('summary', '', this.t('referral_label')));
      const form = text('form', 'bb-rewards__form');
      const label = text('label', '', this.t('referral_label'));
      const id = `${this.root.id}-referral-code`;
      label.htmlFor = id;
      const input = text('input', 'bb-rewards__input');
      Object.assign(input, { id, name: 'referral_code', type: 'text', maxLength: 80, required: true, placeholder: this.t('referral_placeholder'), autoComplete: 'off' });
      const saved = storage.get('bb_rewards_referral_v1');
      if (/^[a-z\d_-]{3,80}$/i.test(saved?.code || '')) input.value = saved.code;
      const button = this.button(this.t('referral_claim'), null, 'wide');
      button.type = 'submit';
      button.style.marginTop = '12px';
      const result = text('div', '');
      form.append(label, input, button, result);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const code = input.value.trim();
        if (!/^[a-z\d_-]{3,80}$/i.test(code)) { result.replaceChildren(this.notice(this.t('referral_invalid'), 'error')); return; }
        if (!this.account.csrfToken) { result.replaceChildren(this.notice(this.t('session_error'), 'error'), this.login()); return; }
        let pending = storage.get('bb_rewards_pending_referral_v1');
        if (!pending || pending.code !== code) {
          pending = { code, key: requestKey() };
          storage.set('bb_rewards_pending_referral_v1', pending);
        }
        button.disabled = true;
        input.disabled = true;
        try {
          const response = await this.api('/referral/claim', { code, csrfToken: this.account.csrfToken, idempotencyKey: pending.key });
          result.replaceChildren(this.notice(this.t('referral_success'), 'success'));
          if (response.code) result.append(this.copyRow(response.code, this.t('copy')));
          storage.remove('bb_rewards_pending_referral_v1');
          storage.remove('bb_rewards_referral_v1');
          window.dispatchEvent(new CustomEvent('bb:rewards-changed', { detail: this.root.id }));
        } catch (error) { result.replaceChildren(this.notice(error.message, 'error')); }
        finally { button.disabled = false; input.disabled = false; }
      });
      details.append(form);
      card.append(details);
      return card;
    }
  }

  const mount = () => {
    document.querySelectorAll('[data-bb-rewards]').forEach(root => {
      if (controllers.has(root)) return;
      try { controllers.set(root, new Rewards(root)); } catch { root.hidden = true; }
    });
  };
  window.BeautyBratsRewards = { mount };
  document.addEventListener('shopify:section:load', mount);
  mount();
})();
